import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Theme,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import { makeStyles } from 'tss-react/mui';
import { CodeRepos } from '../onboarding-types';
import { AdvancedOptionsCheckboxesOptions } from '../onboarding-utils';

const AdvancedOptionsInstructions: FC<
  React.PropsWithChildren<AdvancedOnboardingOptionsProps>
> = ({ repoType, repoDisplayName }) => {
  const { classes } = useStyles();

  const relevantInstructions = Messages[repoType];
  const relevantCheckBoxTitle = CheckBoxTitles[repoType];
  const relevantFooter = Footer[repoType];
  const relevantCheckBoxes = AdvancedOptionsCheckboxesOptions[repoType];

  return (
    <div className={classes.advancedOnboardingOptions}>
      <div className={classes.boxContent}>
        <div className={classes.header}>
          <Typography variant='body1' fontWeight='bold'>
            Login to {repoDisplayName}
          </Typography>
        </div>
        <div className={classes.body}>
          <Typography variant='body1' component='div' color='text.primary'>
            {relevantInstructions}
            <br />
            {relevantCheckBoxTitle}
          </Typography>
        </div>
        <div className={classes.options}>
          <FormGroup>
            {relevantCheckBoxes.map(checkboxLabel => (
              <FormControlLabel
                control={<Checkbox checked />}
                label={checkboxLabel}
                key={checkboxLabel}
              />
            ))}
          </FormGroup>
        </div>

        <div className={classes.footer}>
          <Typography variant='body1' color='text.primary' component='div'>
            {relevantFooter}
          </Typography>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  advancedOnboardingOptions: {
    minWidth: 330,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 15,
    boxShadow: theme.shadows[2],
    textAlign: 'start',
  },
  header: {
    paddingBlockStart: theme.spacing(1.5),
  },
  body: {
    paddingBlockStart: theme.spacing(1.5),
    whiteSpace: 'pre-line',
  },
  boxContent: {
    maxWidth: 270,
    alignItems: 'center',
    margin: 'auto',
    paddingBlockStart: theme.spacing(1),
  },
  options: {
    paddingBlockStart: theme.spacing(1),
  },
  footer: {
    paddingBlockStart: theme.spacing(1),
    paddingBlockEnd: theme.spacing(1.5),
  },
}));

interface AdvancedOnboardingOptionsProps {
  repoType: CodeRepos;
  repoDisplayName: string;
}
export default AdvancedOptionsInstructions;

const Messages = {
  [CodeRepos.GitHub]: (
    <Typography color='text.primary'>
      Open <b>GitHub</b> and go to <b>Settings</b>. <br />
      Under settings choose <b>Developer settings</b> and go to{' '}
      <b>Personal access tokens</b>. <br />
      Click the <b>Generate a personal access token</b> button.
    </Typography>
  ),
  [CodeRepos.GitLab]: (
    <Typography color='text.primary'>
      Open <b>Gitlab</b>. In the top-right corner, select your avatar and select{' '}
      <b>Edit profile</b>. <br />
      On the left sidebar, click <b>Access Tokens</b>.
    </Typography>
  ),
  [CodeRepos.AzureCode]: (
    <Typography color='text.primary'>
      Open <b>Azure</b> and sign in to your organization (https://dev.azure.com/
      yourorganization) From your home page, open <b>user settings</b> (appears
      left to your avatar) and then select <b>Personal access tokens</b>.
      <br />
      Select <b>+ New Token</b>.
    </Typography>
  ),
  [CodeRepos.Bitbucket]: (
    <Typography color='text.primary'>
      Log into Bitbucket cloud, and click your avatar to open the user menu,
      then select <b>Personal setting</b>. <br />
      In the sidebar select <b>App passwords</b> and click{' '}
      <b>Create app password</b>. <br />
      In the <b>Add app password</b> add <b>Label</b> of app password.
    </Typography>
  ),
};

const CheckBoxTitles = {
  [CodeRepos.GitHub]: (
    <Typography color='text.primary'>
      Set expiration time to <b>No expiration</b> and choose the following
      checkboxes:
    </Typography>
  ),
  [CodeRepos.GitLab]: (
    <Typography color='text.primary'>
      Enter a <b>Token name</b> and optional <b>Expiration date</b> for the
      token and choose the following checkboxes:
    </Typography>
  ),
  [CodeRepos.AzureCode]: (
    <Typography color='text.primary'>
      Enter a <b>Name</b>, the <b>Organization</b> and optional{' '}
      <b>Expiration</b> for the token and choose the following checkboxes:
    </Typography>
  ),
  [CodeRepos.Bitbucket]: (
    <Typography color='text.primary'>
      In the <b>Add app password</b> add <b>Label</b> of app password and choose
      the following checkboxes:
    </Typography>
  ),
};

const Footer = {
  [CodeRepos.GitHub]: (
    <Typography color='text.primary'>
      Click <b>Generate</b> and copy key to the field on the left.
    </Typography>
  ),
  [CodeRepos.GitLab]: (
    <Typography color='text.primary'>
      Click <b>Create personal access token</b> and save the personal access
      token somewhere safe. <br />
      After you leave the page, you no longer have access to the token.
    </Typography>
  ),
  [CodeRepos.AzureCode]: (
    <Typography color='text.primary'>
      Click <b>Create</b>. <br />
      When you're done, make sure to copy the token and store it in a secure
      location. <br />
      For your security, it won't be shown again.
    </Typography>
  ),
  [CodeRepos.Bitbucket]: (
    <Typography color='text.primary'>
      Copy the generated password and either record or paste it into the
      application you want to give access. <br />
      The password is only displayed this one time.
    </Typography>
  ),
};
